export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  category: string;
  content: string;
  slug: string;
  image?: string;
  socialImage?: string; // Dedicated social sharing image (1200×627px, JPG/PNG recommended)
  published?: boolean; // If false, post is hidden in production
  // Video-specific fields
  video?: {
    youtubeUrl: string;
    type: 'explainer' | 'demo';
  };
  // Podcast-specific fields
  podcastEpisode?: {
    episodeNumber: number | string;
    guestName?: string;
    guestTitle?: string;
    guestBio?: string;
    guestPhoto?: string;
    guestWebsite?: string;
    guestTwitter?: string;
    guestLinkedIn?: string;
    youtubeUrl?: string;
    spotifyUrl?: string;
    appleUrl?: string;
    transcript?: string;
  };
}

// Import all JSON files from the blog posts directory
// Vite requires explicit imports for dynamic file loading
const blogModules = import.meta.glob('../content/blog/posts/*.json', {
  import: 'default',
  eager: true,
});

export function getAllPosts(): BlogPost[] {
  const posts: BlogPost[] = [];
  const isProduction = import.meta.env.PROD;

  for (const [path, data] of Object.entries(blogModules)) {
    // Extract slug from filename (e.g., "getting-started.json" -> "getting-started")
    const slug = path.split('/').pop()?.replace('.json', '') || '';

    const postData = data as Omit<BlogPost, 'slug'>;

    // In production, skip posts that are explicitly marked as unpublished
    // In development, show all posts
    if (isProduction && postData.published === false) {
      continue;
    }

    posts.push({
      ...postData,
      slug,
    });
  }

  // Sort by date (newest first)
  return posts.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });
}

export function getPostBySlug(slug: string): BlogPost | undefined {
  const posts = getAllPosts();
  return posts.find((post) => post.slug === slug);
}

export function getPostById(id: string): BlogPost | undefined {
  const posts = getAllPosts();
  return posts.find((post) => post.id === id);
}
